import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { cn } from '@/lib';
import {
  SHORT_TERM_CARE_DAILY_BENEFIT_AMOUNTS,
  useMaxMonthlyPremium,
  useObservedMaxMonthlyPremium,
  useSelectedDailyBenefitAmounts,
  useShortTermCareFiltersActions,
  useShortTermCareInflationOption,
} from '@/features/shop-insurance';

/** 'any' keeps the Select controlled — Radix treats '' as "no value". */
export const NO_PREMIUM_LIMIT = 'any';



/**
 * Ceiling options spanning the quotes actually on offer.
 *
 * Derived rather than fixed: premium scales hard with issue age, the home care
 * rider adds roughly a third, and the 5% inflation rider another ~40% on top,
 * so any hardcoded list is wrong for most profiles. A fixed list topping out at
 * $300 silently hid real quotes up to $362 for CO age 72, and up to $506 with
 * inflation elected.
 */
export function shortTermCarePremiumLimits(observedMax: number | null): number[] {
  if (!observedMax || observedMax <= 0) return [];

  // Five ceilings across the range, snapped up to the nearest $25 so they read
  // as choices rather than as arithmetic.
  const snapUp = (value: number) => Math.ceil(value / 25) * 25;
  return [...new Set([0.25, 0.4, 0.55, 0.7, 0.85].map(fraction => snapUp(observedMax * fraction)))];
}

export interface ShortTermCareFiltersProps {
  /**
   * Rendered in the sidebar, while the count it displays is derived in
   * ShortTermCareShopView — so the count arrives as a prop while the filter
   * values themselves come from the store both components share.
   */
  quoteCount?: number;
}

/**
 * Filters for the short-term care view.
 *
 * Deliberately separate from ShopInsuranceFiltersSidebar: that sidebar filters
 * life and traditional-LTC quotes on axes short-term care does not have (death
 * benefit, inflation type, benefit duration in years, carrier mix). These two
 * axes — daily benefit and premium — are the ones an advisor shops STC on.
 */
export function ShortTermCareFilters({ quoteCount }: ShortTermCareFiltersProps) {
  const selectedDailyBenefitAmounts = useSelectedDailyBenefitAmounts();
  const maxMonthlyPremium = useMaxMonthlyPremium();
  const observedMaxMonthlyPremium = useObservedMaxMonthlyPremium();
  const premiumLimits = shortTermCarePremiumLimits(observedMaxMonthlyPremium);
  const inflationOption = useShortTermCareInflationOption();
  const {
    toggleDailyBenefitAmount,
    setAllDailyBenefitAmounts,
    setMaxMonthlyPremium: onMaxMonthlyPremiumChange,
    setInflationOption: onInflationOptionChange,
  } = useShortTermCareFiltersActions();

  const allSelected =
    selectedDailyBenefitAmounts.length === SHORT_TERM_CARE_DAILY_BENEFIT_AMOUNTS.length;
  const dailyBenefitLabel = allSelected
    ? 'All amounts'
    : selectedDailyBenefitAmounts.length === 1
      ? `$${selectedDailyBenefitAmounts[0]}/day`
      : `${selectedDailyBenefitAmounts.length} of ${SHORT_TERM_CARE_DAILY_BENEFIT_AMOUNTS.length} selected`;

  return (
    <div
      className="flex flex-col gap-5"
      data-testid="short-term-care-filters"
    >
      <div>
        <h3 className="text-primary-dark mb-2 text-sm font-semibold">Daily Benefit</h3>
        {/* Same shape as the Carriers filter above: a checkbox menu rather than
            a Select, since Radix Select is single-valued. onSelect is
            prevented so the menu survives each toggle. */}
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full focus-visible:outline-none">
            <Button
              variant="outline"
              className="w-full justify-between font-normal"
              data-testid="short-term-care-filters-daily-benefit"
            >
              <span className="truncate">{dailyBenefitLabel}</span>
              <svg
                className="size-4 shrink-0 opacity-50"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width)"
            align="start"
          >
            <DropdownMenuItem
              onSelect={event => {
                event.preventDefault();
                setAllDailyBenefitAmounts(!allSelected);
              }}
              data-testid="short-term-care-filters-daily-benefit-all"
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {SHORT_TERM_CARE_DAILY_BENEFIT_AMOUNTS.map(amount => (
              <DropdownMenuCheckboxItem
                key={amount}
                checked={selectedDailyBenefitAmounts.includes(amount)}
                onCheckedChange={() => toggleDailyBenefitAmount(amount)}
                onSelect={event => event.preventDefault()}
                data-testid={`short-term-care-filters-daily-benefit-${amount}`}
              >
                ${amount}/day
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {selectedDailyBenefitAmounts.length === 0 && (
          <p className="text-muted-foreground mt-2 text-xs">
            Select at least one daily benefit amount to see quotes.
          </p>
        )}
      </div>

      <div>
        <h3 className="text-primary-dark mb-2 text-sm font-semibold">Max Monthly Premium</h3>
        <Select
          value={maxMonthlyPremium === null ? NO_PREMIUM_LIMIT : String(maxMonthlyPremium)}
          onValueChange={value =>
            onMaxMonthlyPremiumChange(value === NO_PREMIUM_LIMIT ? null : Number(value))
          }
        >
          <SelectTrigger
            className="w-full"
            data-testid="short-term-care-filters-max-premium"
          >
            <SelectValue placeholder="No limit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_PREMIUM_LIMIT}>No limit</SelectItem>
            {maxMonthlyPremium !== null && !premiumLimits.includes(maxMonthlyPremium) && (
              <SelectItem value={String(maxMonthlyPremium)}>Up to ${maxMonthlyPremium}/mo</SelectItem>
            )}
            {premiumLimits.map(limit => (
              <SelectItem
                key={limit}
                value={String(limit)}
              >
                Up to ${limit}/mo
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <h3 className="text-primary-dark mb-2 text-sm font-semibold">Inflation Protection</h3>
        {/* Single-valued rather than a multi-select: every election is stored
            under both options, so showing both at once would render each
            configuration twice, about 40% apart in premium. */}
        <div className="flex flex-wrap gap-2">
          {(
            [
              { value: 'none', label: 'None' },
              { value: 'simple5', label: '5% Simple' },
            ] as const
          ).map(option => {
            const isSelected = inflationOption === option.value;

            return (
              <Button
                key={option.value}
                type="button"
                size="sm"
                variant={isSelected ? 'default' : 'outline'}
                aria-pressed={isSelected}
                className={cn('h-8 px-3 text-xs', isSelected && 'bg-primary hover:bg-primary-hover')}
                data-testid={`short-term-care-filters-inflation-${option.value}`}
                onClick={() => onInflationOptionChange(option.value)}
              >
                {option.label}
              </Button>
            );
          })}
        </div>
        {inflationOption === 'simple5' && (
          <p className="text-muted-foreground mt-2 text-xs">
            Benefits grow 5% of the elected amount each year from the 6th policy anniversary,
            capped at twice the elected benefit.
          </p>
        )}
      </div>

      {quoteCount !== undefined && (
        <p
          className="text-muted-foreground text-xs"
          data-testid="short-term-care-filters-count"
        >
          {quoteCount} {quoteCount === 1 ? 'quote' : 'quotes'}
        </p>
      )}
    </div>
  );
}
